/**
 * bind.ts — the data-bind / data-action convention engine.
 * =====================================================================
 *
 * This is the contract between Jordan's hand-authored HTML and the engineered
 * data layer. Jordan writes declarative attributes; this engine does the rest.
 * He never writes `fetch`, a URL, or a field name the API contract lacks.
 *
 * ── data-bind="path" ────────────────────────────────────────────────────────
 * Renders a value from a typed model into an element's text. The path is a
 * dotted lookup against a model object you pass to `applyBindings(root, model)`:
 *
 *     <span data-bind="job.short_name"></span>      → model.job.short_name
 *     <span data-bind="job.provider_status_display"></span>
 *
 * Optional `data-bind-attr="title"` writes into an attribute instead of text:
 *
 *     <a data-bind="job.short_name" data-bind-attr="data-id"></a>
 *
 * Because the model is typed (WorkOrder from the generated client) and the
 * contract-usage lint (tests/contract-usage-lint.mjs) checks every `job.*` path
 * against the openapi.yaml ProviderWorkOrder schema, a bind to a non-existent or
 * forbidden field (e.g. `job.fmv_2`) FAILS THE BUILD, not at runtime.
 *
 * ── data-action="name" ──────────────────────────────────────────────────────
 * Wires a clickable/keyboard-operable element to one of the typed wrappers in
 * `client.ts` (the `actions` registry). Event-DELEGATED from a single listener
 * on the root — so there are ZERO inline `onclick=` handlers and the strict CSP
 * (`script-src 'self'`) holds. The element supplies its target via
 * `data-id="<short_name>"` (or inherits it from the nearest ancestor that has
 * one):
 *
 *     <button data-action="accept" data-id="MNG-1044">Accept</button>
 *
 * Delegation also fixes the prototype's keyboard-a11y P0s: we listen for both
 * `click` and `keydown` (Enter/Space), so non-button elements styled as actions
 * are still operable.
 *
 * The engine intentionally knows NOTHING about specific screens — screens are
 * Web Components that own their own DOM and call `applyBindings` / `wireActions`
 * on themselves.
 */
import { actions, type ActionName } from "./api/client";

/** Resolve a dotted path ("job.site") against a model. Returns undefined if any segment is missing. */
function resolvePath(model: unknown, path: string): unknown {
  let cur: unknown = model;
  for (const seg of path.split(".")) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[seg];
  }
  return cur;
}

function toText(value: unknown): string {
  if (value == null) return "";
  return String(value);
}

/**
 * Render every `[data-bind]` under `root` from `model`.
 * Re-runnable: call again after the model changes to refresh.
 */
export function applyBindings(root: ParentNode, model: unknown): void {
  const nodes = root.querySelectorAll<HTMLElement>("[data-bind]");
  nodes.forEach((el) => {
    const path = el.dataset.bind;
    if (!path) return;
    const value = resolvePath(model, path);
    const attr = el.dataset.bindAttr;
    if (attr) {
      el.setAttribute(attr, toText(value));
    } else {
      el.textContent = toText(value);
    }
  });
}

/** Detail passed to an action-result callback so a screen can refresh/route. */
export interface ActionEvent {
  action: ActionName;
  id: string;
  element: HTMLElement;
}

export interface WireActionsOptions {
  /** Called after a successful action (e.g. to re-fetch + re-render). */
  onSuccess?: (ev: ActionEvent, result: unknown) => void;
  /** Called if the action throws (auth redirects are handled in http.ts). */
  onError?: (ev: ActionEvent, error: unknown) => void;
  /**
   * Optional per-action body builder. For form-driven transitions (onsite /
   * complete-onsite / sow) the screen returns a typed body collected from its
   * own inputs; for the CSV uploads (inventory / fmv) it returns a FormData built
   * from a real <input type=file>. Returning `undefined` fires the action with no
   * body (the simple transitions: accept / decline / schedule / finalize).
   *
   * Returning `false` ABORTS the action without firing it or surfacing an error —
   * used when a required field/file is missing (the screen shows its own message).
   */
  getBody?: (ev: ActionEvent) => unknown | false;
}

function findActionTarget(start: HTMLElement): { el: HTMLElement; action: string } | null {
  let el: HTMLElement | null = start;
  while (el) {
    if (el.dataset && el.dataset.action) return { el, action: el.dataset.action };
    el = el.parentElement;
  }
  return null;
}

/** Read the work-order id from the element or its nearest ancestor with data-id. */
function resolveId(el: HTMLElement): string | null {
  const owner = el.closest<HTMLElement>("[data-id]");
  return owner?.dataset.id ?? null;
}

function isActionName(name: string): name is ActionName {
  return name in actions;
}

/**
 * Attach ONE delegated listener to `root` that routes `data-action` clicks (and
 * Enter/Space key presses) to the typed `client.ts` wrappers. CSP-safe: no
 * inline handlers anywhere. Returns a disposer to remove the listeners.
 */
export function wireActions(root: HTMLElement, opts: WireActionsOptions = {}): () => void {
  async function handle(target: HTMLElement): Promise<void> {
    const hit = findActionTarget(target);
    if (!hit) return;

    const { el, action } = hit;
    if (!isActionName(action)) {
      // A typo or an action not in the registry — surface loudly in dev.
      console.error(`[bind] unknown data-action="${action}" — not in client.ts actions registry`);
      return;
    }

    const id = resolveId(el);
    if (!id) {
      console.error(`[bind] data-action="${action}" has no resolvable data-id`);
      return;
    }

    const ev: ActionEvent = { action, id, element: el };

    // Let the screen build a typed body / FormData for this action (or abort).
    const body = opts.getBody ? opts.getBody(ev) : undefined;
    if (body === false) return; // screen-level validation declined to fire

    el.setAttribute("aria-busy", "true");
    try {
      // The registry is `(id, body?) => Promise`. Simple transitions ignore the
      // second arg; form/upload actions consume it. Calling with (id, body) is
      // safe for all entries.
      const fn = actions[action] as (id: string, body?: unknown) => Promise<unknown>;
      const result = await fn(id, body);
      opts.onSuccess?.(ev, result);
    } catch (error) {
      opts.onError?.(ev, error);
    } finally {
      el.removeAttribute("aria-busy");
    }
  }

  function onClick(e: Event): void {
    void handle(e.target as HTMLElement);
  }
  function onKeydown(e: KeyboardEvent): void {
    if (e.key !== "Enter" && e.key !== " ") return;
    const hit = findActionTarget(e.target as HTMLElement);
    if (!hit) return;
    e.preventDefault();
    void handle(e.target as HTMLElement);
  }

  root.addEventListener("click", onClick);
  root.addEventListener("keydown", onKeydown);
  return () => {
    root.removeEventListener("click", onClick);
    root.removeEventListener("keydown", onKeydown);
  };
}
